/* GPS. One of the sacred trio, so it runs continuously while a capture is open
 * rather than being sampled once on save.
 *
 * The rule from the spec: LOCK at ≤10m, show "settling" above it — but never
 * block the save. A reading at ±40m with the accuracy recorded is worth
 * infinitely more than a reading you didn't take because the app was waiting.
 * gps_accuracy_m is stored on every record, always, for exactly this reason.
 */

import { useEffect, useRef, useState } from 'react';

export const LOCK_THRESHOLD_M = 10;

export interface Fix {
  lat: number;
  lon: number;
  accuracy: number;
  altitude: number | null;
  at: number;
}

export type GpsPhase = 'idle' | 'settling' | 'locked' | 'denied' | 'unavailable';

export interface GpsState {
  fix: Fix | null;
  phase: GpsPhase;
  /** Best (lowest-accuracy-number) fix seen this session. */
  best: Fix | null;
  refix: () => void;
}

export function useGps(active: boolean): GpsState {
  const [fix, setFix] = useState<Fix | null>(null);
  const [best, setBest] = useState<Fix | null>(null);
  const [phase, setPhase] = useState<GpsPhase>('idle');
  const watchId = useRef<number | null>(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    if (!active) return;
    if (!('geolocation' in navigator)) {
      setPhase('unavailable');
      return;
    }

    setPhase((p) => (p === 'locked' ? p : 'settling'));

    watchId.current = navigator.geolocation.watchPosition(
      (pos) => {
        const next: Fix = {
          lat: pos.coords.latitude,
          lon: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
          altitude: pos.coords.altitude,
          at: pos.timestamp,
        };
        setFix(next);
        setBest((b) => (!b || next.accuracy < b.accuracy ? next : b));
        setPhase(next.accuracy <= LOCK_THRESHOLD_M ? 'locked' : 'settling');
      },
      (err) => {
        setPhase(err.code === err.PERMISSION_DENIED ? 'denied' : 'unavailable');
      },
      { enableHighAccuracy: true, maximumAge: 0, timeout: 30_000 },
    );

    return () => {
      if (watchId.current !== null) navigator.geolocation.clearWatch(watchId.current);
      watchId.current = null;
    };
  }, [active, nonce]);

  return {
    fix,
    best,
    phase,
    refix: () => {
      setBest(null);
      setNonce((n) => n + 1);
    },
  };
}

/* Formatting lives in /shared so the desk renders coordinates
   character-for-character the same way. Re-exported here for convenience. */
export { formatCoord, formatAccuracy, metersToFeet } from '@kilroy/shared';
